/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useState } from 'preact/hooks';
import { ClearSelectionButton } from './ClearSelectionButton';
import { Layer } from '#UNSAFE_Layer';
import { Floating } from '#UNSAFE_Floating';

/**
 * The default offset for clear selection mainAxis.
 */
const DEFAULT_OFFSET = 18;
type Props = {
  clearSelection: () => void;
};
type SelectionMode = 'none' | 'single' | 'multiple';

export function useClearSelection<K>({ clearSelection }: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const coordinates = useRef({ x: 0, y: 0 });

  const tryToggleClearButton = (
    event: PointerEvent,
    key: K | undefined,
    selectionMode: SelectionMode,
    selectedIds: Set<K>
  ) => {
    if (
      event.pointerType === 'touch' &&
      key === undefined &&
      selectionMode === 'multiple' &&
      selectedIds.size > 1
    ) {
      setIsVisible(!isVisible);
      coordinates.current = { x: event.clientX, y: event.clientY };
      return true;
    }
    return false;
  };

  const offsetValue = { mainAxis: 2 * DEFAULT_OFFSET };
  const anchorRef = { current: coordinates.current };

  const clearSelectionContent = isVisible ? (
    <Layer>
      <Floating
        anchorRef={anchorRef}
        offsetValue={offsetValue}
        placement="top"
        flipOptions={{ crossAxis: false, mainAxis: false }}
        shiftOptions={{ crossAxis: true, mainAxis: true }}>
        <ClearSelectionButton
          onAction={() => {
            setIsVisible(false);
            clearSelection();
          }}
        />
      </Floating>
    </Layer>
  ) : null;

  return {
    clearSelectionContent,
    tryToggleClearButton
  };
}
